
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Leaf, Recycle, Zap, Droplets, Download, TrendingUp, Target } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SustainabilityMetric {
  id: string;
  name: string;
  category: string;
  currentValue: number;
  targetValue: number;
  unit: string;
  trend: 'improving' | 'declining' | 'stable';
  lastUpdated: string;
}

export default function SustainabilityTracker() {
  const [selectedPeriod, setSelectedPeriod] = useState("current-quarter");
  
  const [metrics, setMetrics] = useState<SustainabilityMetric[]>([
    {
      id: '1',
      name: 'Carbon Footprint',
      category: 'Emissions',
      currentValue: 245,
      targetValue: 200,
      unit: 'tCO2e',
      trend: 'improving',
      lastUpdated: '2024-01-31'
    },
    {
      id: '2',
      name: 'Energy Consumption',
      category: 'Energy',
      currentValue: 8500,
      targetValue: 7500,
      unit: 'kWh',
      trend: 'improving',
      lastUpdated: '2024-01-31'
    },
    {
      id: '3',
      name: 'Water Usage',
      category: 'Water',
      currentValue: 1200,
      targetValue: 1000,
      unit: 'L',
      trend: 'stable',
      lastUpdated: '2024-01-31'
    },
    {
      id: '4',
      name: 'Waste Diverted from Landfill',
      category: 'Waste',
      currentValue: 85,
      targetValue: 90,
      unit: '%',
      trend: 'improving',
      lastUpdated: '2024-01-31'
    },
    {
      id: '5',
      name: 'Renewable Energy Usage',
      category: 'Energy',
      currentValue: 60,
      targetValue: 80,
      unit: '%',
      trend: 'improving',
      lastUpdated: '2024-01-31'
    },
    {
      id: '6',
      name: 'Sustainable Materials',
      category: 'Materials',
      currentValue: 70,
      targetValue: 85,
      unit: '%',
      trend: 'stable',
      lastUpdated: '2024-01-31'
    }
  ]);

  const getTrendColor = (trend: SustainabilityMetric['trend']) => {
    switch (trend) {
      case 'improving': return 'text-green-600';
      case 'declining': return 'text-red-600';
      case 'stable': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend: SustainabilityMetric['trend']) => {
    switch (trend) {
      case 'improving': return '↗';
      case 'declining': return '↘';
      case 'stable': return '→';
      default: return '→';
    }
  };

  const getPerformancePercentage = (current: number, target: number, isReverse: boolean = false) => {
    if (isReverse) {
      // For metrics where lower is better (e.g., carbon footprint, energy consumption)
      return Math.min(100, ((target / current) * 100));
    } else {
      // For metrics where higher is better (e.g., renewable energy %, waste diversion %)
      return Math.min(100, ((current / target) * 100));
    }
  };

  const sustainabilityScore = metrics.reduce((sum, metric) => {
    const isReverse = ['Carbon Footprint', 'Energy Consumption', 'Water Usage'].includes(metric.name);
    return sum + getPerformancePercentage(metric.currentValue, metric.targetValue, isReverse);
  }, 0) / metrics.length;

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Leaf className="h-8 w-8 text-primary" />
            Sustainability Tracker
          </h1>
          <p className="text-muted-foreground">
            Monitor environmental impact and sustainability performance across all project activities
          </p>
        </div>

        <div className="flex gap-4 mb-6">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current-month">Current Month</SelectItem>
              <SelectItem value="current-quarter">Current Quarter</SelectItem>
              <SelectItem value="current-year">Current Year</SelectItem>
              <SelectItem value="last-year">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            <TabsTrigger value="initiatives">Initiatives</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{sustainabilityScore.toFixed(1)}</div>
                    <p className="text-sm text-muted-foreground">Sustainability Score</p>
                    <Progress value={sustainabilityScore} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">245</div>
                    <p className="text-sm text-muted-foreground">tCO2e Emissions</p>
                    <div className="flex items-center justify-center mt-1">
                      <span className="text-xs text-green-600">↓ 18% from last quarter</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">60%</div>
                    <p className="text-sm text-muted-foreground">Renewable Energy</p>
                    <div className="flex items-center justify-center mt-1">
                      <span className="text-xs text-blue-600">↑ 12% from last quarter</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">85%</div>
                    <p className="text-sm text-muted-foreground">Waste Diverted</p>
                    <div className="flex items-center justify-center mt-1">
                      <span className="text-xs text-purple-600">↑ 5% from last quarter</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Sustainability Goals Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {metrics.slice(0, 4).map((metric) => {
                      const isReverse = ['Carbon Footprint', 'Energy Consumption', 'Water Usage'].includes(metric.name);
                      const progress = getPerformancePercentage(metric.currentValue, metric.targetValue, isReverse);
                      
                      return (
                        <div key={metric.id} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{metric.name}</span>
                            <div className="text-right">
                              <span className="text-sm">
                                {metric.currentValue} / {metric.targetValue} {metric.unit}
                              </span>
                            </div>
                          </div>
                          <Progress value={progress} className="w-full" />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{progress.toFixed(1)}% of target</span>
                            <span className={getTrendColor(metric.trend)}>
                              {getTrendIcon(metric.trend)} {metric.trend}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Environmental Impact Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Emissions', 'Energy', 'Water', 'Waste', 'Materials'].map((category) => {
                      const categoryMetrics = metrics.filter(m => m.category === category);
                      const categoryScore = categoryMetrics.length > 0 
                        ? categoryMetrics.reduce((sum, metric) => {
                            const isReverse = ['Carbon Footprint', 'Energy Consumption', 'Water Usage'].includes(metric.name);
                            return sum + getPerformancePercentage(metric.currentValue, metric.targetValue, isReverse);
                          }, 0) / categoryMetrics.length
                        : 0;
                      
                      return (
                        <div key={category} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {category === 'Emissions' && <Zap className="h-4 w-4 text-yellow-600" />}
                            {category === 'Energy' && <Zap className="h-4 w-4 text-blue-600" />}
                            {category === 'Water' && <Droplets className="h-4 w-4 text-blue-400" />}
                            {category === 'Waste' && <Recycle className="h-4 w-4 text-green-600" />}
                            {category === 'Materials' && <Leaf className="h-4 w-4 text-green-500" />}
                            <span className="font-medium">{category}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress value={categoryScore} className="w-20" />
                            <span className="text-sm w-12">{categoryScore.toFixed(0)}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="metrics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sustainability Metrics Dashboard</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {metrics.map((metric) => {
                    const isReverse = ['Carbon Footprint', 'Energy Consumption', 'Water Usage'].includes(metric.name);
                    const progress = getPerformancePercentage(metric.currentValue, metric.targetValue, isReverse);
                    
                    return (
                      <Card key={metric.id}>
                        <CardContent className="pt-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-sm">{metric.name}</h4>
                              <Badge variant="outline">{metric.category}</Badge>
                            </div>
                            
                            <div className="text-center">
                              <div className="text-2xl font-bold text-primary">
                                {metric.currentValue} {metric.unit}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Target: {metric.targetValue} {metric.unit}
                              </div>
                            </div>
                            
                            <Progress value={progress} className="w-full" />
                            
                            <div className="flex justify-between items-center text-xs">
                              <span>{progress.toFixed(1)}% of target</span>
                              <span className={`flex items-center gap-1 ${getTrendColor(metric.trend)}`}>
                                <span>{getTrendIcon(metric.trend)}</span>
                                <span>{metric.trend}</span>
                              </span>
                            </div>
                            
                            <div className="text-xs text-muted-foreground">
                              Last updated: {new Date(metric.lastUpdated).toLocaleDateString()}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="initiatives" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Active Sustainability Initiatives</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Solar Panel Installation</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Installing 500kW solar panel system to increase renewable energy usage
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-blue-100 text-blue-800">Energy</Badge>
                        <span className="text-sm">Progress: 75%</span>
                      </div>
                      <Progress value={75} className="mt-2" />
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Waste Reduction Program</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Comprehensive waste minimization and recycling program
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-green-100 text-green-800">Waste</Badge>
                        <span className="text-sm">Progress: 60%</span>
                      </div>
                      <Progress value={60} className="mt-2" />
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Water Conservation System</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Rainwater harvesting and greywater recycling system
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-blue-100 text-blue-800">Water</Badge>
                        <span className="text-sm">Progress: 40%</span>
                      </div>
                      <Progress value={40} className="mt-2" />
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold">Green Building Certification</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        BREEAM Excellent certification for new construction
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <Badge className="bg-green-100 text-green-800">Materials</Badge>
                        <span className="text-sm">Progress: 85%</span>
                      </div>
                      <Progress value={85} className="mt-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Environmental Impact Reduction</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">18%</div>
                        <div className="text-sm text-green-700">CO2 Reduction</div>
                        <div className="text-xs text-green-600 mt-1">vs. last year</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">25%</div>
                        <div className="text-sm text-blue-700">Energy Savings</div>
                        <div className="text-xs text-blue-600 mt-1">vs. baseline</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">15%</div>
                        <div className="text-sm text-purple-700">Water Savings</div>
                        <div className="text-xs text-purple-600 mt-1">vs. last year</div>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">30%</div>
                        <div className="text-sm text-orange-700">Waste Reduction</div>
                        <div className="text-xs text-orange-600 mt-1">vs. baseline</div>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-semibold mb-3">Key Achievements</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span>Achieved carbon neutral operations</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Installed 500kW renewable energy capacity</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span>Diverted 85% of waste from landfill</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <span>Reduced water consumption by 15%</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sustainability Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Environmental Impact Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Carbon Footprint Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Sustainability Dashboard</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>ESG Performance Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Renewable Energy Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Waste Management Report</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
